import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  MessageSquare, 
  FolderOpen, 
  TrendingUp, 
  Calendar,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalUsers: number;
  totalBlogs: number;
  totalMessages: number;
  totalCaseStudies: number;
  monthlyViews: number;
  activeUsers: number;
}

interface RecentActivity {
  id: string;
  type: 'blog' | 'message' | 'user' | 'case_study';
  title: string;
  description: string;
  timestamp: string;
  status?: 'published' | 'draft' | 'pending';
}

interface ChartData {
  month: string;
  blogs: number;
  views: number;
  messages: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalBlogs: 0,
    totalMessages: 0,
    totalCaseStudies: 0,
    monthlyViews: 0,
    activeUsers: 0
  });
  
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Mock data - In real app, this would fetch from API
    const mockStats: DashboardStats = {
      totalUsers: 156,
      totalBlogs: 42,
      totalMessages: 89,
      totalCaseStudies: 18,
      monthlyViews: 12450,
      activeUsers: 23
    };
    
    const mockActivity: RecentActivity[] = [
      {
        id: '1',
        type: 'blog',
        title: 'New Blog Post Published',
        description: '"The Future of Web Development" has been published',
        timestamp: '2024-01-15T10:30:00Z',
        status: 'published'
      },
      {
        id: '2',
        type: 'message',
        title: 'New Contact Message',
        description: 'John Doe sent a project inquiry',
        timestamp: '2024-01-15T09:15:00Z'
      },
      {
        id: '3',
        type: 'case_study',
        title: 'Case Study Updated',
        description: 'E-commerce Platform case study has been updated',
        timestamp: '2024-01-14T16:45:00Z',
        status: 'published'
      },
      {
        id: '4',
        type: 'user',
        title: 'New Admin User',
        description: 'Sarah Wilson has been added as content manager',
        timestamp: '2024-01-14T14:20:00Z'
      },
      {
        id: '5',
        type: 'blog',
        title: 'Draft Saved',
        description: '"Mobile App Development Trends" draft saved',
        timestamp: '2024-01-14T11:30:00Z',
        status: 'draft'
      }
    ];
    
    const mockChartData: ChartData[] = [
      { month: 'Jul', blogs: 8, views: 2400, messages: 12 },
      { month: 'Aug', blogs: 12, views: 3200, messages: 18 },
      { month: 'Sep', blogs: 6, views: 2800, messages: 15 },
      { month: 'Oct', blogs: 10, views: 4100, messages: 22 },
      { month: 'Nov', blogs: 14, views: 3800, messages: 19 },
      { month: 'Dec', blogs: 16, views: 4500, messages: 25 }
    ];
    
    // Simulate loading
    setTimeout(() => {
      setStats(mockStats);
      setRecentActivity(mockActivity);
      setChartData(mockChartData);
      setIsLoading(false);
    }, 1000);
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'blog':
        return FileText;
      case 'message':
        return MessageSquare;
      case 'user':
        return Users;
      case 'case_study':
        return FolderOpen;
      default:
        return Activity;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'published':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'draft':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'pending':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'positive' as const,
      link: '/admin/users'
    },
    {
      title: 'Blog Posts',
      value: stats.totalBlogs,
      icon: FileText,
      color: 'bg-green-500',
      change: '+8%',
      changeType: 'positive' as const,
      link: '/admin/blogs'
    },
    {
      title: 'Messages',
      value: stats.totalMessages,
      icon: MessageSquare,
      color: 'bg-yellow-500',
      change: '+23%',
      changeType: 'positive' as const,
      link: '/admin/messages'
    },
    {
      title: 'Case Studies',
      value: stats.totalCaseStudies,
      icon: FolderOpen,
      color: 'bg-purple-500',
      change: '+5%',
      changeType: 'positive' as const,
      link: '/admin/case-studies'
    }
  ];

  const quickActions = [
    {
      title: 'Create New Blog',
      description: 'Write and publish a new blog post',
      icon: FileText,
      color: 'bg-blue-500',
      link: '/admin/blogs/new'
    },
    {
      title: 'Add Case Study',
      description: 'Showcase a new project',
      icon: FolderOpen,
      color: 'bg-green-500',
      link: '/admin/case-studies/new'
    },
    {
      title: 'Manage Users',
      description: 'Add or edit admin users',
      icon: Users,
      color: 'bg-purple-500',
      link: '/admin/users'
    },
    {
      title: 'View Messages',
      description: 'Check new contact messages',
      icon: MessageSquare,
      color: 'bg-yellow-500',
      link: '/admin/messages'
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Welcome back! Here's what's happening with your website.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <Link
                key={index}
                to={card.link}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {card.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {card.value.toLocaleString()}
                    </p>
                    <div className="flex items-center mt-2">
                      <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                      <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                        {card.change}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                        vs last month
                      </span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Chart Section */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Analytics Overview
                </h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Blogs</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Views</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Messages</span>
                  </div>
                </div>
              </div>
              
              {/* Simple Chart Representation */}
              <div className="space-y-4">
                {chartData.map((data, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-12 text-sm text-gray-600 dark:text-gray-400 font-medium">
                      {data.month}
                    </div>
                    <div className="flex-1 flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${(data.blogs / 20) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(data.views / 5000) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${(data.messages / 30) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 w-20 text-right">
                      {data.views.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Quick Actions
              </h2>
              <div className="space-y-4">
                {quickActions.map((action, index) => {
                  const IconComponent = action.icon;
                  return (
                    <Link
                      key={index}
                      to={action.link}
                      className="flex items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mr-4`}>
                        <IconComponent className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {action.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {action.description}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Recent Activity
            </h2>
            <Link 
              to="/admin/activity" 
              className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
            >
              View All
            </Link>
          </div>
          
          <div className="space-y-4">
            {recentActivity.map((activity) => {
              const IconComponent = getActivityIcon(activity.type);
              return (
                <div key={activity.id} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <IconComponent className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.title}
                      </h3>
                      {activity.status && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                          {activity.status}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {activity.description}
                    </p>
                    <div className="flex items-center mt-2">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;